package com.essntl.features.supplier.ui.addeditsupplier

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.proposal.domain.repository.ProposalRepository
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.supplier.domain.model.SupplierModel
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.toastmanager.ToastManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

class AddEditSupplierViewModel(
    private val supplierRepository: SupplierRepository,
    private val categoryRepository: CategoryRepository,
    private val areaRepository: AreaRepository,
    private val proposalRepository: ProposalRepository,
    private val itineraryRepository: ItineraryRepository,
    private val serviceRepository: ServiceRepository,
    private val toastManager: ToastManager,
    private val state: MutableStateFlow<AddEditSupplierState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    init {
        getAllCategoryList()
        getAllAreaList()
    }

    fun onEvent(event: AddEditSupplierEvent) {
        when (event) {
            is AddEditSupplierEvent.GetSupplier -> getSupplier(event)
            is AddEditSupplierEvent.UpdateSupplierFields -> updateSupplierFields(event)
            is AddEditSupplierEvent.AddSupplier -> addSupplier()
            is AddEditSupplierEvent.EditSupplier -> editSupplier()
            is AddEditSupplierEvent.ClearSupplierState -> clearSupplierState()
            is AddEditSupplierEvent.ClearSuccessState -> clearSuccessState()
        }
    }

    private fun getAllCategoryList() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllAreaList() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private var getSupplierJob: Job? = null
    private fun getSupplier(event: AddEditSupplierEvent.GetSupplier) {
        if (state.value.getSupplier.data?.id == event.supplierId) return

        state.value.supplier.value = SupplierModel.empty.copy(id = event.supplierId)

        // Make sure to cancel the previous job
        // The user can switch between ta quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getSupplierJob?.cancel()
        getSupplierJob = getData(
            getData = { supplierRepository.get(event.supplierId) },
            getState = { state.value.getSupplier },
            setState = {
                state.value = state.value.copy(
                    getSupplier = it,
                    isEditEnabled = false,
                )
            },
            onSuccess = { supplier ->
                state.value.supplier.value = supplier
                getProposalServices(supplier.id)
                getAllService(supplier.id)
            },
        )
    }

    private fun getAllService(id: String) =
        getData(
            getData = { serviceRepository.getBySupplierId(id) },
            getState = { state.value.getAllService },
            setState = { newState -> state.update { state -> state.copy(getAllService = newState) } },
        )

    private fun getProposalServices(supplierId: String) =
        getData(
            getData = { proposalRepository.getAllProposalServiceBySupplierId(supplierId = supplierId) },
            getState = { state.value.getProposalServices },
            setState = { state.value = state.value.copy(getProposalServices = it) },
            onSuccess = { proposalServices ->
                if (proposalServices.isNotEmpty()) {
                    getItineraries(proposalServices.mapNotNull { it.itineraryId })
                }
            },
        )

    private fun getItineraries(ids: List<String>) =
        getData(
            getData = { itineraryRepository.getAllByIds(ids) },
            getState = { state.value.getItineraries },
            setState = { state.value = state.value.copy(getItineraries = it) },
        )

    private fun updateSupplierFields(event: AddEditSupplierEvent.UpdateSupplierFields) {
        state.value.supplier.value = event.supplier
        val newSupplier = state.value.getSupplier.data?.getChangedFields(state.value.supplier.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newSupplier.isEmpty)
        }
    }

    private fun addSupplier() =
        getData(
            getData = { supplierRepository.insert(state.value.supplier.value) },
            getState = { state.value.addEditSupplier },
            setState = { state.value = state.value.copy(addEditSupplier = it) },
            onSuccess = { supplier ->
                state.update {
                    it.copy(
                        getSupplier = State.success(supplier),
                    )
                }
                state.value.supplier.value = supplier

                toastManager.success(
                    description = "Supplier created successfully",
                )
            },
        )

    private fun editSupplier() {
        val newSupplier = state.value.getSupplier.data?.getChangedFields(state.value.supplier.value) ?: return

        if (newSupplier.isEmpty) return

        getData(
            getData = {
                supplierRepository.update(
                    newSupplier.copy(
                        area = state.value.supplier.value.area,
                        category = state.value.supplier.value.category,
                    ),
                )
            },
            getState = { state.value.addEditSupplier },
            setState = { state.value = state.value.copy(addEditSupplier = it) },
            onSuccess = { supplier ->
                state.update {
                    it.copy(
                        getSupplier = State.success(supplier),
                    )
                }
                state.value.supplier.value = supplier

                toastManager.success(
                    description = "Supplier updated successfully",
                )
            },
        )
    }

    private fun clearSuccessState() {
        state.value = state.value.copy(
            addEditSupplier = State(),
            isEditEnabled = false,
        )
    }

    private fun clearSupplierState() {
        state.value = state.value.copy(
            getSupplier = State(),
            addEditSupplier = State(),
            isEditEnabled = false,
        )
        state.value.supplier.value = SupplierModel.empty
    }
}